export type CmsLinkKind = 'custom' | 'page' | 'block'

export const PAGE_LINK_PREFIX = 'page:'
export const EVENT_LINK_PREFIX = 'event:'
export const PROGRAM_LINK_PREFIX = 'program:'
export const BLOCK_LINK_PREFIX = 'block:'
export const ASSET_LINK_PREFIX = 'asset:'
/** `|` cannot appear unencoded in a url, so the suffix is unambiguous. */
export const NEW_TAB_SUFFIX = '|newtab'

export const hasNewTabFlag = (value: string): boolean => value.endsWith(NEW_TAB_SUFFIX)

export const stripNewTabFlag = (value: string): string =>
    hasNewTabFlag(value) ? value.slice(0, -NEW_TAB_SUFFIX.length) : value

/** An empty link never carries the flag — "no link yet" stays the plain empty string. */
export const withNewTabFlag = (value: string, newTab: boolean): string => {
    const base = stripNewTabFlag(value)
    return newTab && base !== '' ? `${base}${NEW_TAB_SUFFIX}` : base
}

/**
 * Pages are referenced by id, never by slug: renaming a page must not break every link pointing at
 * it, and a deleted target resolves to an empty string so the renderer degrades to unlinked text
 * rather than emitting a href that 404s.
 */
export const getPageId = (value: string): string | undefined =>
    value.startsWith(PAGE_LINK_PREFIX)
        ? stripNewTabFlag(value).slice(PAGE_LINK_PREFIX.length)
        : undefined

const getPrefixedId = (value: string, prefix: string): string | undefined =>
    value.startsWith(prefix) ? stripNewTabFlag(value).slice(prefix.length) : undefined

export const getEventId = (value: string): string | undefined =>
    getPrefixedId(value, EVENT_LINK_PREFIX)

export const getProgramId = (value: string): string | undefined =>
    getPrefixedId(value, PROGRAM_LINK_PREFIX)

export const toEventPath = (eventId: string): string => `/events/${encodeURIComponent(eventId)}`

export const toProgramPath = (programId: string): string =>
    `/lifestyle-programs/${encodeURIComponent(programId)}`

export type CmsLinkEntityIds = {
    eventIds: ReadonlySet<string>
    programIds: ReadonlySet<string>
}

export const EMPTY_LINK_ENTITY_IDS: CmsLinkEntityIds = {
    eventIds: new Set(),
    programIds: new Set(),
}

export const resolveEntityLink = (
    value: string,
    { eventIds, programIds }: CmsLinkEntityIds,
): string | undefined => {
    const eventId = getEventId(value)
    if (eventId !== undefined) {
        return eventIds.has(eventId)
            ? withNewTabFlag(toEventPath(eventId), hasNewTabFlag(value))
            : ''
    }
    const programId = getProgramId(value)
    if (programId !== undefined) {
        return programIds.has(programId)
            ? withNewTabFlag(toProgramPath(programId), hasNewTabFlag(value))
            : ''
    }
    return undefined
}

export const getBlockId = (value: string): string | undefined =>
    value.startsWith(BLOCK_LINK_PREFIX)
        ? stripNewTabFlag(value).slice(BLOCK_LINK_PREFIX.length)
        : undefined

/**
 * `asset:` is deliberately not one of the three link kinds: it is never offered by the link
 * selector, only by the rich-text link popover. Storing the id rather than the public url is what
 * keeps the media delete guard able to see the reference.
 */
export const getAssetId = (value: string): string | undefined =>
    value.startsWith(ASSET_LINK_PREFIX)
        ? stripNewTabFlag(value).slice(ASSET_LINK_PREFIX.length)
        : undefined

export const getLinkKind = (value: string): CmsLinkKind => {
    if (getPageId(value) !== undefined) return 'page'
    if (getEventId(value) !== undefined) return 'page'
    if (getProgramId(value) !== undefined) return 'page'
    if (getBlockId(value) !== undefined) return 'block'
    return 'custom'
}

/**
 * The pure deep rewrite the admin preview needs. The server does the same thing with a batched
 * lookup; the admin already holds the page list, so it resolves locally.
 *
 * A missing id must resolve to an empty string here too — if the preview and the live render
 * disagree about a broken link, editors stop trusting the preview.
 */
export const resolvePageLinksDeep = <T>(
    value: T,
    slugByPageId: ReadonlyMap<string, string>,
    entityIds: CmsLinkEntityIds = EMPTY_LINK_ENTITY_IDS,
): T => {
    if (typeof value === 'string') {
        const entityHref = resolveEntityLink(value, entityIds)
        if (entityHref !== undefined) return entityHref as T
        const id = getPageId(value)
        if (id === undefined) return value
        const slug = slugByPageId.get(id)
        // The new-tab suffix rides through the rewrite; only render time consumes it.
        return (slug === undefined ? '' : withNewTabFlag(`/${slug}`, hasNewTabFlag(value))) as T
    }

    if (Array.isArray(value)) {
        return value.map((item) => resolvePageLinksDeep(item, slugByPageId, entityIds)) as T
    }

    if (value !== null && typeof value === 'object') {
        const prototype = Object.getPrototypeOf(value)
        // Only descend into plain objects, so class instances such as Date survive intact.
        if (prototype === Object.prototype || prototype === null) {
            return Object.fromEntries(
                Object.entries(value).map(([key, item]) => [
                    key,
                    resolvePageLinksDeep(item, slugByPageId, entityIds),
                ]),
            ) as T
        }
    }

    return value
}
